import { Control } from "react-hook-form";
import { FormInput } from "../../../../components/ui/Input/FormInput/FormInput";
import { Card, CardContent, CardHeader } from "../../../../components/ui/common/card";
import { customEmailValidation, getSupplierContactNameError, validateCharacterLength } from "../../../../utils/utils";

interface SupplierInfoCardProps {
  control: Control<any>;
  isSupplierAsUser: boolean;
}

const EYEBROW_CLASS =
  "tw-text-[10px] tw-uppercase tw-tracking-wider tw-text-muted-foreground tw-font-medium";

/**
 * "Supplier info" card — same fields/order/validation as the legacy
 * `SuppllierRegisterForm` (Supplier Name*, Supplier Contact Name*, Email,
 * Contact Number). Shared between the supplier Edit drawer
 * (`lcaSupplier/supplierEdit`) and the "register a new supplier inline"
 * form used by the Ingredient register flow — both just pass their own RHF
 * `control` instance.
 */
const SupplierInfoCard = ({ control, isSupplierAsUser }: SupplierInfoCardProps) => {
  return (
    <Card className="tw-gap-4 tw-p-5">
      <CardHeader className="tw-p-0">
        <span className={EYEBROW_CLASS}>Supplier info</span>
      </CardHeader>
      <CardContent className="tw-grid tw-grid-cols-1 tw-gap-4 tw-p-0 sm:tw-grid-cols-2">
        <FormInput
          control={control}
          name="supplier_name"
          label="Supplier Name"
          required
          placeholder="Supplier Name"
          errorMessage="Supplier Name can't be empty!"
          className="tw-h-9 tw-text-sm"
          labelClassName="tw-text-xs"
          rules={{
            validate: (value?: string) =>
              validateCharacterLength(value ?? "", 32, "Supplier name cannot exceed 32 characters")
          }}
        />
        <FormInput
          control={control}
          name="supplier_contact_name"
          label="Supplier Contact Name"
          required
          placeholder="Supplier Contact Name"
          errorMessage="Supplier Contact Name can't be empty!"
          className="tw-h-9 tw-text-sm"
          labelClassName="tw-text-xs"
          rules={{
            validate: (value?: string) => getSupplierContactNameError(value) ?? true
          }}
        />
        <FormInput
          control={control}
          name="supplier_email"
          label={isSupplierAsUser ? "Email*" : "Email"}
          placeholder="Email"
          type="email"
          className="tw-h-9 tw-text-sm"
          labelClassName="tw-text-xs"
          rules={{
            required: isSupplierAsUser ? "Supplier email is required" : false,
            validate: (value?: string) => (value ? customEmailValidation(value) : true)
          }}
        />
        <FormInput
          control={control}
          name="supplier_phone"
          label="Contact Number"
          placeholder="Contact Number"
          className="tw-h-9 tw-text-sm tw-font-mono"
          labelClassName="tw-text-xs"
          rules={{
            validate: (value?: string) =>
              !value ||
              value.length <= 20 ||
              "Supplier Contact Number must be less than 20 characters!"
          }}
        />
      </CardContent>
    </Card>
  );
};

export default SupplierInfoCard;
